 
 
import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  Button,
  MenuDots,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
// import FlaggedProvision from '../../atoms/Icons/FlaggedProvision';
import BookMarkIcon from '../../atoms/Icons/BookmarkIcon';

const useStyles = makeStyles()({
  tableGrid: {
    border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    borderBottom: 'none',
    borderRadius: '0.25rem',
    overflow: 'hidden',
    position: 'relative',
  },
  headerRow: {
    background: patientAccessTheme.palette.grey[100],
    height: '48px',
  },
  headerCell: {
    padding: '5px 16px',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  sectionRow: {
    backgroundColor: patientAccessTheme.palette.grey[700],
    height: '38px',
  },
  sectionCell: {
    padding: '0 16px',
    color: patientAccessTheme.palette.status.white,
  },
  dataRow: {
    backgroundColor: patientAccessTheme.palette.status.white,
    '&:hover $editCell': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  dataCell: {
    padding: '12px 16px',
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[200]}`,
    position: 'relative',
  },
  placeholderCell: {
    width: 60,
    minWidth: 60,
    maxWidth: 60,
    padding: 0,
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[200]}`,
    position: 'relative',
  },
  editCell: {
    width: '100%',
    height: '100%',
    backgroundColor: patientAccessTheme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.25s ease',
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto',
    },
  },
});

export interface Column {
  name: string;
  width: string;
  key: string;
  isFlag?: boolean;
}

export interface RowGroup<T = any> {
  title?: string;
  items: T[];
}

export interface FlaggedProvisionListProps {
  columns: Column[];
  rows: RowGroup<any>[];
  tableWidth?: string;
  onFlagToggle?: (groupIndex: number, itemIndex: number) => void;
  editMenuItems?: { label: string; icon?: React.ReactNode }[];
  onEditAction?: (
    action: string,
    groupIndex: number,
    itemIndex: number,
  ) => void;

  /** 👉 New Callback */
  onAddParameter?: (groupIndex: number) => void;
}

export const FlaggedProvisionList: React.FC<FlaggedProvisionListProps> = ({
  columns,
  rows,
  tableWidth = '100%',
  onFlagToggle,
  editMenuItems = [],
  onEditAction,
  onAddParameter,
}) => {
  const { classes } = useStyles();

  return (
    <Grid className={classes.tableGrid} style={{ width: tableWidth }}>
      <Table>
        <TableHead>
          <TableRow className={classes.headerRow}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                className={classes.headerCell}
                style={{ width: col.width }}
              >
                <Typography
                  fontSize="12px"
                  fontType="medium"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {col.name}
                </Typography>
              </TableCell>
            ))}
            <TableCell className={classes.headerCell} style={{ width: 60 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((group, groupIndex) => (
            <React.Fragment key={groupIndex}>
              {group.title && (
                <TableRow className={classes.sectionRow}>
                  <TableCell
                    colSpan={columns.length + 1}
                    className={classes.sectionCell}
                  >
                    <Grid
                      container
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography fontSize="14px" fontType="semiBold">
                        {group.title}
                      </Typography>

                      {/* 👉 Add Parameter (NEW LOGIC ONLY) */}
                      <Button
                        label={'+ Add Parameter'}
                        variant="text"
                        onClick={() => onAddParameter?.(groupIndex)}
                        styleProps={{
                          color: patientAccessTheme.palette.status.white,
                          width: '157px',
                          height: '32px',
                        }}
                      />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}

              {group.items.length > 0 ? (
                group.items.map((item: any, itemIndex: number) => (
                  <TableRow key={itemIndex} className={classes.dataRow}>
                    {columns.map((col) => (
                      <TableCell key={col.key} className={classes.dataCell}>
                        {col.isFlag ? (
                          <Grid
                            container
                            sx={{
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              onClick={() =>
                                onFlagToggle?.(groupIndex, itemIndex)
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              <BookMarkIcon
                                color={
                                  item[col.key] === true
                                    ? '#ED9B74'
                                    : patientAccessTheme.palette.grey[300]
                                }
                              />
                            </span>
                          </Grid>
                        ) : (
                          item[col.key] ?? '-'
                        )}
                      </TableCell>
                    ))}

                    {/* stable menu column */}
                    <TableCell className={classes.placeholderCell}>
                      <div className={classes.editCell}>
                        <MenuDots
                          menuItems={editMenuItems}
                          data={item}
                          handleMenuItemClick={(action) =>
                            onEditAction?.(action, groupIndex, itemIndex)
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className={classes.dataRow}>
                  <TableCell
                    colSpan={columns.length + 1}
                    className={classes.dataCell}
                  >
                    <Typography
                      fontSize="14px"
                      fontType="regular"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                    >
                      No items
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Grid>
  );
};

export default FlaggedProvisionList;
